import React from 'react';
import ReactPaginate from 'react-paginate';
import {useLoaderData, Await, useNavigate, useLocation} from '@remix-run/react';

//domains
import {loader} from '~/routes/($lang)/collections/$collectionHandle';
import {
  PRODUCT_PER_PAGE,
  PRODUCT_PER_PAGE_WITHOUT_ADS,
} from 'domains/collections';

export default function CollectionPagination() {
  const {totalProducts, hasAds, translation} = useLoaderData<typeof loader>();
  const productPerPage = hasAds
    ? PRODUCT_PER_PAGE
    : PRODUCT_PER_PAGE_WITHOUT_ADS;
  const pageCount = Math.ceil(totalProducts / productPerPage);

  const location = useLocation();
  const params = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const navigate = useNavigate();

  // react-paginate renders `<a role="button">` with NO href unless it is given
  // a hrefBuilder, so there was no crawlable route past the first page of any
  // collection — on a 439-product collection that hid 91% of the products
  // from anything that doesn't run JavaScript.
  //
  // Build the URL by cloning the params: filterInputToParams() mutates the
  // URLSearchParams it is handed, and hrefBuilder runs once per control on
  // every render.
  const buildHref = React.useCallback(
    (pageNumber: number) => {
      const next = new URLSearchParams(params);
      if (pageNumber <= 1) next.delete('page');
      else next.set('page', String(pageNumber));
      const qs = next.toString();
      return `${location.pathname}${qs ? `?${qs}` : ''}`;
    },
    [params, location.pathname],
  );

  // Controlled highlight: sort/filter clicks reset the page param to 1, and
  // an uncontrolled ReactPaginate would keep the stale page highlighted.
  const rawPage = parseInt(params.get('page') ?? '1', 10);
  const currentPage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const forcePage = Math.min(currentPage - 1, Math.max(pageCount - 1, 0));

  return (
    <>
      <div className="mb-20 collection-pagination hidden lg:block">
        <ReactPaginate
          breakLabel="..."
          forcePage={forcePage}
          // hrefAllControls extends the href to the prev/next controls too.
          hrefAllControls
          hrefBuilder={(pageNumber) => buildHref(pageNumber)}
          nextLabel={translation.next ? `${translation.next} >` : 'next >'}
          onPageChange={(e) => {
            navigate(buildHref(e.selected + 1));
          }}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel={
            translation.previous ? `< ${translation.previous}` : '< previous'
          }
          renderOnZeroPageCount={null}
          marginPagesDisplayed={0}
        />
      </div>
      {/* The control above is `hidden lg:block` — display:none under
          mobile-first indexing, where the listing is JS infinite scroll. This
          keeps every page reachable for a mobile crawler without touching the
          visual design. */}
      {pageCount > 1 && (
        <nav className="sr-only lg:hidden" aria-label="All pages">
          <ul>
            {Array.from({length: pageCount}, (_, i) => (
              <li key={i}>
                <a href={buildHref(i + 1)}>{`Page ${i + 1}`}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );
}
